import React from 'react';
import { useTranslation } from 'react-i18next';

import TotalCards from './TotalCards';
import MediaValueByAccType from './MediaValueByAccType';
import TopLines from '@app/home/TopLines';
import ReFilters from '@app/components/filters';

import ErrorBoundary from '@app/ErrorBoundary';
import Crashed from '@app/components/errors/CrashedComponent';
import CrashedFullHeight from '@app/components/errors/CrashedComponentFullHeight';
import { Typography, Flex, Collapse, ConfigProvider, Row, Col } from '@app/ui';

import { FILTER_ORIGIN } from '@app/constants/filters';
import './overview.scss';

type OverviewProps = any;

const Overview = ({
    home,
    client,
    selectAccType,
    accTypeSelected,
    syncTopPlatformsData,
    syncTopPlacementsData,
    rightsholders,
    canAccessFilterRedesign,
}: OverviewProps) => {
    const { t } = useTranslation('home');

    return (
        <div data-test="overview-section">
            {canAccessFilterRedesign && (
                <ErrorBoundary FallbackComponent={Crashed}>
                    <ReFilters origin={FILTER_ORIGIN.HOME} />
                </ErrorBoundary>
            )}
            <ConfigProvider
                theme={{
                    components: {
                        Collapse: {
                            headerBg: '#FFF',
                            colorBorder: '#FFF',
                            headerPadding: '12px 16px 8px 4px',
                            contentPadding: '16px 0 0 0',
                        },
                    },
                }}
            >
                <Collapse
                    expandIconPosition="end"
                    defaultActiveKey={['1']}
                    expandIcon={({ isActive }) => (
                        <i
                            className={`fa-solid fa-chevron-${
                                isActive ? 'up' : 'down'
                            }`}
                            style={{ fontSize: '16px', color: '#515252' }}
                        />
                    )}
                    style={{
                        borderRadius: '0',
                        zIndex: '1',
                    }}
                    items={[
                        {
                            key: '1',
                            label: (
                                <Typography.Title
                                    level={4}
                                    style={{
                                        margin: 0,
                                        color: '#1f1f1f',
                                        fontWeight: 400,
                                    }}
                                >
                                    {t('overview.highlights')}
                                </Typography.Title>
                            ),
                            headerClass: 'highlight-header-collapse',
                            children: (
                                <Flex dir="row">
                                    <div
                                        className="gds-flex-grid__item gds-flex-grid__item--desktop-2 gds-flex-grid__item--tablet-1 gds-flex-grid__item--mobile-1 -m-b-3"
                                        data-test="metric-tiles"
                                    >
                                        <ErrorBoundary
                                            FallbackComponent={
                                                CrashedFullHeight
                                            }
                                        >
                                            <TotalCards
                                                cards={home.cards}
                                                client={client}
                                            />
                                        </ErrorBoundary>
                                    </div>
                                    <div className="gds-flex-grid__item gds-flex-grid__item--desktop-2 gds-flex-grid__item--tablet-1 gds-flex-grid__item--mobile-1 -m-b-3">
                                        <div
                                            className="gds-flex-grid__item--full-height"
                                            data-test="media-value-by-acc-type"
                                        >
                                            <ErrorBoundary
                                                FallbackComponent={
                                                    CrashedFullHeight
                                                }
                                            >
                                                <MediaValueByAccType
                                                    mediaValueByAccType={
                                                        home.mediaValueByAccType
                                                    }
                                                    selectAccType={
                                                        selectAccType
                                                    }
                                                    accTypeSelected={
                                                        accTypeSelected
                                                    }
                                                />
                                            </ErrorBoundary>
                                        </div>
                                    </div>
                                </Flex>
                            ),
                        },
                    ]}
                />
            </ConfigProvider>
            <Row gutter={16} style={{ marginTop: '32px' }}>
                <Col md={24}>
                    <ErrorBoundary FallbackComponent={Crashed}>
                        <TopLines
                            graph={home.topPlacement.graph}
                            total={home.topPlacement.total}
                            sync={syncTopPlacementsData}
                            rightsholders={rightsholders}
                            type="placement"
                            currency={client.currency?.code}
                        />
                    </ErrorBoundary>
                </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: '32px' }}>
                <Col md={24}>
                    <ErrorBoundary FallbackComponent={Crashed}>
                        <TopLines
                            graph={home.topPlatform.graph}
                            total={home.topPlatform.total}
                            sync={syncTopPlatformsData}
                            type="platform"
                            currency={client.currency?.code}
                        />
                    </ErrorBoundary>
                </Col>
            </Row>
        </div>
    );
};

export default Overview;
